/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {useMemo} from 'react';
import {makeStyles} from '@material-ui/core';
import isNil from 'lodash.isnil';
import {ChargeLevelIcon} from '@ion/dls/src/icons/ChargeLevelIcon';
import {DLSTheme} from '@/types/';
import {dlsAlt, dlsColor} from '../../theme/theme';
import {getChargeLevelColor, validateChargeLevel} from './chargeLevelUtils';

interface StyleProps {
  isBatteryCell?: boolean;
};

export interface ChargeLevelProps extends StyleProps{
  charge: number;
  isCardVariant?: boolean;
}

const useStyles = makeStyles((theme: DLSTheme) => {
  return {
    chargeText : {
      ...dlsAlt('typography', 'cta2'),
      color      : `${dlsColor('white')} !important`,
      fontWeight : '700 !important',
      padding    : '0 4px'
    },
    cardVariant : {
      fontSize    : '14px',
      fontFamily  : 'Roboto',
      lineHeight  : ({isBatteryCell}: StyleProps) => `${isBatteryCell?'21px':'24px'}`,
      fontWeight  : 400,
      paddingLeft : '4px',
      color       : `${dlsColor('grey2')}`
    },
    batteryCell : {
      marginTop : '10%',
      padding   : '0px !important',
      display   : 'flex'
    }
  };
});

/**
 * ChargeLevel component is used to display the charge level of the asset / device.
 * 
 * ```typescript
 * 
 * import ChargeLevel from '@ion/dls/src/components/chargelevel/ChargeLevel';
 * 
 * <ChargeLevel charge={80} />
 * <ChargeLevel charge={evChargeLevel} />
 * <ChargeLevel charge={deviceChargeLevel} />
 * 
 * ```
 * @param charge - Charge level of the asset / device.
 */
const ChargeLevel = ({charge, isCardVariant = false, isBatteryCell = false}: ChargeLevelProps) => {
  const classes      = useStyles({isBatteryCell});
  const chargeLevel  = useMemo(() => validateChargeLevel(charge), [charge]);
  
  if (!isNil(charge)) {
    return (
      <>
        <ChargeLevelIcon size={isCardVariant ? 22 : 18}
          color={getChargeLevelColor(chargeLevel)}
          outlineColor={isCardVariant ? 'none' : dlsColor('white')} />
        <span className={isCardVariant ? classes.cardVariant : classes.chargeText}>
          {chargeLevel}%
        </span>
      </>
    );
  }
  return null;
};

export default ChargeLevel;
